import * as SMS from 'src/constants/sms'

/**
 * What one character costs after smart encoding, on GSM-7 and on UCS-2, and whether it forces UCS-2
 * @param {string} char - A single code point
 * @returns {{gsm: number, ucs2: number, isUnicode: boolean}}
 */
const measureChar = (char) => {
  const unicode = char.codePointAt(0).toString(16).padStart(4, '0')

  // GSM 03.38's extension table is still 7-bit, but each of its characters travels as an
  // escape plus the character itself, so it costs two characters on GSM-7.
  if (SMS.GSM_7BIT_EXTENDED_CHARS.includes(char) || SMS.SMART_ENCODING_TO_GSM_7BIT_EXTENDED_CHARS.includes(unicode)) {
    return { gsm: 2, ucs2: 1, isUnicode: false }
  }

  // The rest of GSM 03.38 costs one character and keeps the message on GSM-7 — including
  // the accented and Greek characters, which sit above ASCII but are not Unicode escapes.
  if (SMS.GSM_7BIT_BASIC_CHARS.includes(char)) {
    return { gsm: 1, ucs2: 1, isUnicode: false }
  }

  // check if this UCS-2 character is replaceable with ASCII character with more than one character
  // for example => ½ will be replaced by 1/2 which is 3 characters and not 1
  const smartCharCount = SMS.SMART_ENCODING_CHAR_COUNT.find((entry) => entry.key === unicode)
  if (smartCharCount) {
    return { gsm: smartCharCount.value, ucs2: smartCharCount.value, isUnicode: false }
  }

  if (SMS.SMART_ENCODING_CHARS.includes(unicode)) {
    return { gsm: 1, ucs2: 1, isUnicode: false }
  }

  return { gsm: char.length, ucs2: char.length, isUnicode: true }
}

/**
 * Length of an SMS body as the carrier counts it, mirroring api-core's SmsHelper::carrierBodyLength(): on GSM-7 an
 * extension character counts as 2; once any character needs UCS-2, every character counts in UTF-16 code units (an
 * emoji is 2 or more). "…" counts as 3 ("...")
 * @param {string} text
 * @returns {{length: number, extraChars: number, hasUnicode: boolean}} extraChars is length beyond String.length
 */
export function measureSmsBody (text) {
  let gsm = 0
  let ucs2 = 0
  let hasUnicode = false

  for (const char of text) {
    const measured = measureChar(char)
    gsm += measured.gsm
    ucs2 += measured.ucs2
    hasUnicode = hasUnicode || measured.isUnicode
  }

  const length = hasUnicode ? ucs2 : gsm

  return { length, extraChars: length - text.length, hasUnicode }
}

/**
 * @param {string} text
 * @returns {number}
 */
export function carrierBodyLength (text) {
  return measureSmsBody(text).length
}

/**
 * Vuelidate validator: an empty body passes, anything else must fit SMS_BODY_CHAR_LIMIT as the carrier counts it
 * @param {?string} text
 * @returns {boolean}
 */
export function isWithinCarrierLimit (text) {
  return !text || carrierBodyLength(text) <= SMS.SMS_BODY_CHAR_LIMIT
}

const truncate = (text, limit, isUcs2) => {
  let length = 0
  let truncated = ''

  for (const char of text) {
    const measured = measureChar(char)
    length += isUcs2 ? measured.ucs2 : measured.gsm

    if (length > limit) {
      break
    }

    truncated += char
  }

  return truncated
}

/**
 * The longest start of the text whose carrierBodyLength() fits the limit, cut between code points so an emoji is
 * never split in half. A cut that drops every UCS-2 character turns the rest into GSM-7, so it is measured again.
 * @param {string} text
 * @param {number} limit
 * @returns {string}
 */
export function truncateToCarrierLimit (text, limit) {
  const truncated = truncate(text, limit, measureSmsBody(text).hasUnicode)

  return carrierBodyLength(truncated) > limit ? truncate(truncated, limit, false) : truncated
}
